import { AuthContext } from "@/platforms/customer/context/AuthContext";
import {
  getBlogComments,
  postBlogComment,
} from "@/platforms/customer/services/blogService";
import { mediaUrlFromApi } from "@/utils/mediaUrlFromApi";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

function getInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "?";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase() || s.slice(0, 1).toUpperCase();
}

function formatRelative(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${Math.max(0, sec)} sec ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function readBlogWriterFromStorage() {
  try {
    return JSON.parse(localStorage.getItem("blog_writer") || "null");
  } catch {
    return null;
  }
}

function findTopLevelCommentById(comments, id) {
  if (!id || !Array.isArray(comments)) return null;
  return comments.find((c) => c && String(c.id) === String(id)) || null;
}

function countCommentNodes(comment) {
  if (!comment) return 0;
  let n = 1;
  const replies = Array.isArray(comment.replies) ? comment.replies : [];
  for (const r of replies) n += countCommentNodes(r);
  return n;
}

function countForestNodes(list) {
  if (!Array.isArray(list) || !list.length) return 0;
  let n = 0;
  for (const c of list) n += countCommentNodes(c);
  return n;
}

/**
 * Pick leading top-level threads whose total node count (including nested replies)
 * fits within `budget`. Always includes at least the first thread to avoid an empty preview.
 */
function pickThreadsWithinNodeBudget(topLevel, budget) {
  const cap = Number(budget) || 0;
  if (!Array.isArray(topLevel) || !topLevel.length) return [];
  if (cap <= 0) return [topLevel[0]];

  const out = [];
  let used = 0;
  for (const thread of topLevel) {
    const nodes = countCommentNodes(thread);
    if (!out.length) {
      out.push(thread);
      used += nodes;
      continue;
    }
    if (used + nodes <= cap) {
      out.push(thread);
      used += nodes;
    } else {
      break;
    }
  }
  return out;
}

/** Loose email check: [^\\s@] in a regex *literal* was wrong (blocked any address containing the letter "s"). */
function isValidGuestEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || "").trim());
}

function CommentBlock({
  comment,
  isAuthor,
  replyingToId,
  onReplyClick,
  onRequireBlogWriterLogin,
  depth = 0,
}) {
  const img = comment.authorAvatarUrl
    ? mediaUrlFromApi(comment.authorAvatarUrl)
    : null;
  /** Reply button: blog writer only; only under someone else's top-level comment (not on author's own top comment). */
  const showWriterReply =
    isAuthor && depth === 0 && comment.authorKind !== "author";
  const isReply = depth > 0;

  const card = (
    <div
      className={
        isReply
          ? "rounded-xl border border-black/12 bg-white px-2.5 py-1 min-w-0 flex-1"
          : "rounded-2xl border border-black/15 bg-white px-4 py-2 min-w-0 flex-1"
      }
    >
      <div className="flex items-start gap-2">
        <div
          className={
            isReply
              ? "h-6 w-6 rounded-full overflow-hidden bg-[#FBBC05]/70 border border-black/10 shrink-0 flex items-center justify-center text-[10px] font-semibold text-black"
              : "h-7 w-7 rounded-full overflow-hidden bg-[#FBBC05]/70 border border-black/10 shrink-0 flex items-center justify-center text-[11px] font-semibold text-black"
          }
        >
          {img ? (
            <Image
              src={img}
              alt="author"
              width={28}
              height={28}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span>{getInitials(comment.authorName)}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            <span
              className={
                isReply
                  ? "span-text font-semibold text-black"
                  : "span-text font-semibold text-black"
              }
            >
              {comment.authorName}
            </span>
            <span className="span-text text-black/40">
              {formatRelative(comment.createdAt)}
            </span>
            {comment.authorKind === "author" ? (
              <span
                className={
                  isReply
                    ? "span-text font-semibold uppercase tracking-wide text-[#284078] bg-black/5 px-1.5 py-0.5 rounded"
                    : "span-text font-semibold uppercase tracking-wide text-[#284078] bg-black/5 px-1.5 py-0.5 rounded"
                }
              >
                Author
              </span>
            ) : null}
          </div>
          <p
            className={
              isReply
                ? "mt-0.5 text-[12px] text-black/80 leading-snug whitespace-pre-wrap"
                : "mt-1 text-[13px] text-black/80 leading-snug whitespace-pre-wrap"
            }
          >
            {comment.body}
          </p>
          {showWriterReply ? (
            <div className="mt-1 flex justify-end border-t border-black/5 pt-1">
              <button
                type="button"
                onClick={() =>
                  onReplyClick(replyingToId === comment.id ? null : comment.id)
                }
                className="btn-text font-semibold text-[#284078] hover:text-[#1E2B55] hover:underline underline-offset-2 cursor-pointer"
              >
                {replyingToId === comment.id ? "Cancel" : "Reply"}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={
        depth > 0
          ? "mt-2 ml-6 sm:ml-10 pl-3 border-l-2 border-[#FBBC05]/40"
          : ""
      }
    >
      {card}
      {Array.isArray(comment.replies) && comment.replies.length
        ? comment.replies.map((r) => (
            <CommentBlock
              key={r.id}
              comment={r}
              isAuthor={isAuthor}
              replyingToId={replyingToId}
              onReplyClick={onReplyClick}
              onRequireBlogWriterLogin={onRequireBlogWriterLogin}
              depth={depth + 1}
            />
          ))
        : null}
    </div>
  );
}

export const useBlogPostCommentsSection = (blogPublicId) => {
  const router = useRouter();
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [replyingToId, setReplyingToId] = useState(null);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [bwRev, setBwRev] = useState(0);
  const composerRef = useRef(null);

  const writerId = useMemo(() => {
    const s = String(blogPublicId || "");
    const i = s.indexOf(":");
    return i > 0 ? s.slice(0, i) : "";
  }, [blogPublicId]);

  const [tokenRev, setTokenRev] = useState(0);
  const blogWriterToken = useMemo(() => {
    try {
      return String(localStorage.getItem("blog_writer_token") || "").trim();
    } catch {
      return "";
    }
  }, [tokenRev]);

  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const bwWriter = useMemo(() => isMounted ? readBlogWriterFromStorage() : null, [bwRev, isMounted]);
  const isAuthor = useMemo(() => {
    // Only allow "author reply" if a blog-writer session exists AND it matches this blog's writerId.
    if (!bwWriter || !writerId || !blogWriterToken) return false;
    const a = String(bwWriter.id ?? "")
      .trim()
      .toLowerCase();
    const b = String(writerId).trim().toLowerCase();
    return Boolean(a && b && a === b);
  }, [bwWriter, writerId, blogWriterToken]);

  const onRequireBlogWriterLogin = useCallback(() => {
    const returnTo = `${window.location.pathname}${window.location.search}`;
    router.push(`/blog-login?returnTo=${encodeURIComponent(returnTo)}`);
  }, [router]);

  const replyingToComment = useMemo(
    () =>
      replyingToId ? findTopLevelCommentById(comments, replyingToId) : null,
    [comments, replyingToId],
  );

  const replyingPreview = useMemo(() => {
    const raw = String(replyingToComment?.body || "")
      .trim()
      .replace(/\s+/g, " ");
    if (!raw) return "";
    return raw.length > 120 ? `${raw.slice(0, 117)}…` : raw;
  }, [replyingToComment]);

  useEffect(() => {
    if (!replyingToId) return;
    const el = composerRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [replyingToId]);

  useEffect(() => {
    const onBw = () => {
      setBwRev((n) => n + 1);
      setTokenRev((n) => n + 1);
    };
    window.addEventListener("blog_writer_updated", onBw);
    return () => window.removeEventListener("blog_writer_updated", onBw);
  }, []);

  const loadComments = useCallback(async () => {
    if (!blogPublicId) return;
    setLoading(true);
    setLoadErr("");
    try {
      const { comments: list } = await getBlogComments(blogPublicId);
      setComments(Array.isArray(list) ? list : []);
    } catch (e) {
      setLoadErr(e?.message || "Could not load comments");
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [blogPublicId]);

  useEffect(() => {
    void loadComments();
  }, [loadComments]);

  useEffect(() => {
    // Switching posts should reset the preview/expanded UX.
    setCommentsExpanded(false);
  }, [blogPublicId]);

  const customerDisplayName = useMemo(() => {
    if (!user) return "";
    return user.full_name || user.name || user.email?.split("@")[0] || "Reader";
  }, [user]);

  const customerAvatar = user?.avatar_url
    ? mediaUrlFromApi(user.avatar_url)
    : null;

  const runPost = async (nameForGuest, emailOverride) => {
    const text = String(draft || "").trim();
    if (!text) return;
    const parentId = replyingToId || undefined;
    const headers = {};
    if (parentId) {
      const bwTok = String(
        localStorage.getItem("blog_writer_token") || "",
      ).trim();
      if (!bwTok) {
        alert(
          "Log in as the blog author (blog writer account) to post a reply.",
        );
        return;
      }
      // If a different writer is logged in on this browser, do not even attempt the reply.
      if (!isAuthor) {
        alert(
          "Only this blog's author can reply. Please log in with the correct blog writer account.",
        );
        return;
      }
      headers.Authorization = `Bearer ${bwTok}`;
    } else if (isAuthenticated) {
      // Phase 2: cookie session — no Bearer from localStorage
    } else {
      const g = String(nameForGuest || "").trim();
      if (!g) return;
    }

    const emailForRequest =
      emailOverride != null && String(emailOverride).trim() !== ""
        ? String(emailOverride).trim()
        : String(guestEmail || "").trim();

    setSubmitting(true);
    try {
      const data = await postBlogComment(
        blogPublicId,
        {
          body: text,
          guestName:
            !parentId && !isAuthenticated
              ? String(nameForGuest || "").trim()
              : undefined,
          guestEmail:
            !parentId && !isAuthenticated ? emailForRequest : undefined,
          parentId,
        },
        headers,
      );
      setDraft("");
      setReplyingToId(null);
      if (data.heldAsSpam) {
        alert(
          "Your message contained a link. It was saved for review and does not appear publicly. The blog author can see it under Spam Comments.",
        );
      }
      await loadComments();
    } catch (e) {
      alert(e?.message || "Failed to post comment");
    } finally {
      setSubmitting(false);
      setGuestModalOpen(false);
    }
  };

  const onSubmitClick = () => {
    const text = String(draft || "").trim();
    if (!text) return;
    if (replyingToId) {
      void runPost();
      return;
    }
    if (isAuthenticated) {
      void runPost();
      return;
    }
    setGuestModalOpen(true);
  };

  const confirmGuest = () => {
    const g = guestName.trim();
    const e = guestEmail.trim();
    if (!g) return;
    if (!e) return;
    if (!isValidGuestEmail(e)) return;
    void runPost(g, e);
    setGuestName("");
    setGuestEmail("");
  };

  const PREVIEW_NODE_BUDGET = 5;

  const { totalCommentNodes, visibleThreads, hiddenCommentNodes } =
    useMemo(() => {
      const total = countForestNodes(comments);
      const preview = pickThreadsWithinNodeBudget(
        comments,
        PREVIEW_NODE_BUDGET,
      );
      const visible = commentsExpanded ? comments : preview;
      const previewNodes = countForestNodes(preview);
      const hidden = Math.max(0, total - previewNodes);
      return {
        totalCommentNodes: total,
        visibleThreads: visible,
        hiddenCommentNodes: hidden,
      };
    }, [comments, commentsExpanded]);

  const hasMoreComments = !commentsExpanded && hiddenCommentNodes > 0;

  const noCommentsYet = !loading && !loadErr && comments.length === 0;
  const compactCommentsLayout = noCommentsYet && !replyingToId;

  const commentsListClassName = (() => {
    if (loading) return "mt-8 space-y-5";
    if (totalCommentNodes === 0)
      return compactCommentsLayout ? "mt-3" : "mt-8 space-y-3";
    if (commentsExpanded)
      return "mt-8 max-h-[min(760px,82vh)] overflow-y-auto overscroll-y-auto pr-1 space-y-3";
    if (hasMoreComments)
      return "mt-8 max-h-[min(640px,74vh)] overflow-hidden space-y-3";
    return "mt-8 space-y-3";
  })();

  return {
    getInitials,
    isValidGuestEmail,
    CommentBlock,
    isAuthenticated,
    comments,
    loadErr,
    draft,
    setDraft,
    loading,
    submitting,
    guestModalOpen,
    setGuestModalOpen,
    guestName,
    setGuestName,
    guestEmail,
    setGuestEmail,
    replyingToId,
    setReplyingToId,
    commentsExpanded,
    setCommentsExpanded,
    isAuthor,
    onRequireBlogWriterLogin,
    replyingPreview,
    composerRef,
    customerDisplayName,
    customerAvatar,
    onSubmitClick,
    confirmGuest,
    visibleThreads,
    hiddenCommentNodes,
    hasMoreComments,
    compactCommentsLayout,
    commentsListClassName,
  };
};
